import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@libs/zapi-ui/shadcn";
import { AppButton, ConfirmationModal, FigmaTypography } from "@libs/zapi-ui/custom";
import { AddWebLinkModal } from "./AddWebLinkModal";
import { X, MoreHorizontal, Plus, FileUp, Globe, Eye, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { downloadVaultAll, downloadCaseBriefVaultAll } from "@/zapi-frontend/shared/lib/downloadVaultAll";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import $api from "@/zapi-frontend/shared/api/axios";
import { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";

type TCaseBriefVaultDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: TPreUploadedDocument[];
  pendingUploadNames?: string[];
  onAddFiles: (files: FileList) => void;
  onAddWebLink: (url: string) => void;
  onRemove: (documentId: number) => void;
  onViewContent?: (document: TPreUploadedDocument) => void;
};

const formatFileSize = (size?: number) => {
  if (!size) return null;
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
};

const getFileExtension = (fileName: string) => {
  const parts = fileName.split(".");
  if (parts.length < 2) return "file";
  return parts[parts.length - 1].toLowerCase();
};

function ResourceIcon({ document }: { document: TPreUploadedDocument }) {
  if (document.webUrl) {
    return (
      <span className="border border-innoveraZinc-150 rounded-[5px] h-9 w-7 flex items-center justify-center shrink-0">
        <Globe className="size-4 text-innoveraZinc-500" />
      </span>
    );
  }
  const ext = getFileExtension(document.fileName);
  return (
    <span className="border border-innoveraZinc-150 rounded-[5px] h-9 w-7 flex items-center justify-center shrink-0">
      <span className="text-[8px] leading-[10px] font-medium tracking-[-0.16px] text-innoveraZinc-500 uppercase">
        {ext}
      </span>
    </span>
  );
}

function getStatusLabel(document: TPreUploadedDocument, isWebLink: boolean, fileSize: string | null) {
  if (document.status === "error") return "Error processing";
  if (isWebLink) return "Added";
  return fileSize ? `${fileSize} - Uploaded` : "Uploaded";
}

function VaultItem({
  document,
  onRemove,
  onViewContent
}: {
  document: TPreUploadedDocument;
  onRemove: () => void;
  onViewContent?: () => void;
}) {
  const { id: projectId } = useParams();
  const fileSize = formatFileSize(document.fileSize);
  const isWebLink = !!document.webUrl;
  const hasError = document.status === "error";

  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isWebLink || !projectId) return;
    let active = true;
    $api
      .get<{ data: { url: string } }>(`/projects/${projectId}/documents/${document.documentId}`)
      .then((response) => {
        if (active) setFileUrl(response.data.data.url);
      })
      .catch((error) => console.error("Failed to load document url:", error));
    return () => {
      active = false;
    };
  }, [isWebLink, projectId, document.documentId]);

  const href = isWebLink ? document.webUrl : fileUrl;

  return (
    <div className="flex items-center gap-2 py-3 border-b border-innoveraZinc-150 pr-10 relative">
      <div className="flex flex-1 items-center gap-2 min-w-0">
        <ResourceIcon document={document} />
        <div className="flex flex-col flex-1 min-w-0">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-[14px] leading-5 font-medium text-innoveraZinc-900 truncate hover:underline"
            >
              {removeTimestampPrefix(document.fileName)}
            </a>
          ) : (
            <p className="text-[14px] leading-5 font-medium text-innoveraZinc-900 truncate">
              {removeTimestampPrefix(document.fileName)}
            </p>
          )}
          <FigmaTypography
            variant="body-3"
            className={cn("truncate", hasError ? "text-innoveraRed-400" : "text-innoveraZinc-500")}
          >
            {getStatusLabel(document, isWebLink, fileSize)}
          </FigmaTypography>
        </div>
      </div>
      <div className="absolute right-2 top-[19px]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="size-6 rounded-lg flex items-center justify-center hover:bg-innoveraZinc-100"
              aria-label="Actions"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[170px]">
            {onViewContent && (
              <DropdownMenuItem onSelect={onViewContent} className="gap-2">
                <Eye className="size-4" />
                View Content
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onRemove} className="gap-2 text-innoveraRed-400 focus:text-innoveraRed-400">
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function CaseBriefVaultDrawer({
  open,
  onOpenChange,
  documents,
  pendingUploadNames = [],
  onAddFiles,
  onAddWebLink,
  onRemove,
  onViewContent
}: TCaseBriefVaultDrawerProps) {
  const params = useParams();
  const projectId = params.id ?? params.projectId;
  const caseBriefId = useCaseBriefChatStore((s) => s.caseBriefId);
  const canDownloadAll = !!projectId || !!caseBriefId;
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isWebLinkModalOpen, setIsWebLinkModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadAll = async () => {
    if (!projectId && !caseBriefId) return;
    setIsDownloadingAll(true);
    const [, error] = await tryCatch(
      projectId ? downloadVaultAll(projectId) : downloadCaseBriefVaultAll(caseBriefId as number)
    );
    if (error) toast.error("Failed to download files");
    setIsDownloadingAll(false);
  };

  const handleFilesClick = () => {
    setIsAddMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleWebLinkClick = () => {
    setIsAddMenuOpen(false);
    setIsWebLinkModalOpen(true);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    onAddFiles(e.target.files);
    e.target.value = "";
  };

  const handleConfirmDelete = () => {
    if (deleteTargetId === null) return;
    onRemove(deleteTargetId);
    setDeleteTargetId(null);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-[440px] ml-auto h-full flex flex-col">
        <DrawerHeader className="border-b border-innoveraZinc-150 p-4 flex items-center justify-between">
          <DrawerTitle className="text-[18px] font-medium leading-7 tracking-[-0.18px] text-innoveraZinc-900">
            Vault
          </DrawerTitle>
          <div className="flex items-center gap-2">
            {canDownloadAll && (
              <AppButton
                appearance="outlined"
                color="zinc150"
                onClick={handleDownloadAll}
                isLoading={isDownloadingAll}
                loadingText="Downloading..."
                disabled={documents.length === 0}
                startAdornment={<Download className="size-4" />}
                data-testid="vault-download-all"
              >
                Download All
              </AppButton>
            )}
            <DropdownMenu open={isAddMenuOpen} onOpenChange={setIsAddMenuOpen}>
              <DropdownMenuTrigger asChild>
                <AppButton color="zinc950" startAdornment={<Plus className="size-4" />}>
                  Add
                </AppButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="w-[135px] p-1">
                <DropdownMenuItem onSelect={handleFilesClick} className="gap-1 px-3 py-2">
                  <FileUp className="size-4" />
                  Files
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleWebLinkClick} className="gap-1 px-3 py-2">
                  <Globe className="size-4" />
                  Web Link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DrawerClose asChild>
              <button
                type="button"
                className="size-6 rounded flex items-center justify-center hover:bg-innoveraZinc-100"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 relative">
          {documents.length === 0 && pendingUploadNames.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center">
              <p className="text-[14px] text-innoveraZinc-500">No files or web links yet</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {documents.map((doc) => (
                <VaultItem
                  key={doc.documentId}
                  document={doc}
                  onRemove={() => setDeleteTargetId(doc.documentId)}
                  onViewContent={onViewContent ? () => onViewContent(doc) : undefined}
                />
              ))}
              {pendingUploadNames.length > 0 && (
                <div className="flex items-center my-2 text-[12px] leading-[18px] py-2 px-3 bg-innoveraZinc-100 text-innoveraZinc-700 rounded">
                  <div className="h-3 w-3 border-2 border-t-transparent border-innoveraZinc-500 rounded-full animate-spin mr-2 flex-shrink-0" />
                  <span className="truncate">{`Uploading: ${pendingUploadNames.join(", ")}`}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <AddWebLinkModal
          isOpen={isWebLinkModalOpen}
          onClose={() => setIsWebLinkModalOpen(false)}
          onSubmit={onAddWebLink}
        />

        <ConfirmationModal
          isOpen={deleteTargetId !== null}
          onClose={() => setDeleteTargetId(null)}
          onConfirm={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this item?"
          confirmText="Delete"
        />
      </DrawerContent>
    </Drawer>
  );
}
